import { ApiClientError, isRecord, requestJson } from "./http.js";
import { isAdminUser } from "./admin.js";
import type { AdminUser } from "./admin.js";

/** The instance recovery key as /auth/me reports it to every account. */
export interface AdminRecoveryStatus {
  readonly keyId: string;
  readonly publicKeyB64: string;
  readonly enrolled: boolean;
  readonly enrolledAt: number | null;
}

export interface RecoveryAdmin {
  readonly userId: string;
  readonly email: string;
  /** Null until the account has an identity key pair. */
  readonly identityPubB64: string | null;
  readonly holder: boolean;
  readonly grantedAt: number | null;
}

/** The admin view of the instance recovery key. */
export interface RecoveryOverview {
  readonly keyId: string;
  readonly publicKeyB64: string;
  readonly createdAt: number;
  readonly createdByEmail: string | null;
  /** Our own grant; null when we do not hold the key. */
  readonly sealedKeyB64: string | null;
  readonly admins: ReadonlyArray<RecoveryAdmin>;
  readonly enrolled: number;
  readonly accounts: number;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || typeof value === "number";
}

export function isAdminRecoveryStatus(value: unknown): value is AdminRecoveryStatus {
  return isRecord(value) &&
    typeof value["keyId"] === "string" &&
    typeof value["publicKeyB64"] === "string" &&
    typeof value["enrolled"] === "boolean" &&
    isNullableNumber(value["enrolledAt"]);
}

function isRecoveryAdmin(value: unknown): value is RecoveryAdmin {
  return isRecord(value) &&
    typeof value["userId"] === "string" &&
    typeof value["email"] === "string" &&
    isNullableString(value["identityPubB64"]) &&
    typeof value["holder"] === "boolean" &&
    isNullableNumber(value["grantedAt"]);
}

export function isRecoveryOverview(value: unknown): value is RecoveryOverview {
  return isRecord(value) &&
    typeof value["keyId"] === "string" &&
    typeof value["publicKeyB64"] === "string" &&
    typeof value["createdAt"] === "number" &&
    isNullableString(value["createdByEmail"]) &&
    isNullableString(value["sealedKeyB64"]) &&
    Array.isArray(value["admins"]) &&
    value["admins"].every(isRecoveryAdmin) &&
    typeof value["enrolled"] === "number" &&
    typeof value["accounts"] === "number";
}

function unexpected(): ApiClientError {
  return new ApiClientError(500, "Unerwartete Antwort vom Server.");
}

function overviewFrom(body: unknown): RecoveryOverview | null {
  if (!isRecord(body) || !(body["recovery"] === null || isRecoveryOverview(body["recovery"]))) {
    throw unexpected();
  }
  return body["recovery"];
}

export async function fetchRecoveryOverview(): Promise<RecoveryOverview | null> {
  return overviewFrom(await requestJson("/api/v1/admin/recovery"));
}

export async function createInstanceKey(publicKeyB64: string, sealedKeyB64: string): Promise<RecoveryOverview | null> {
  return overviewFrom(await requestJson("/api/v1/admin/recovery", { method: "POST", body: { publicKeyB64, sealedKeyB64 } }));
}

export async function removeInstanceKey(keyId: string): Promise<void> {
  overviewFrom(await requestJson(`/api/v1/admin/recovery?keyId=${encodeURIComponent(keyId)}`, { method: "DELETE" }));
}

export async function grantInstanceKey(userId: string, sealedKeyB64: string): Promise<RecoveryOverview | null> {
  return overviewFrom(await requestJson("/api/v1/admin/recovery/grants", { method: "POST", body: { userId, sealedKeyB64 } }));
}

export async function fetchEscrow(userId: string): Promise<{ keyId: string; escrowB64: string }> {
  const body = await requestJson(`/api/v1/admin/users/${userId}/escrow`);
  if (!isRecord(body) || typeof body["keyId"] !== "string" || typeof body["escrowB64"] !== "string") {
    throw unexpected();
  }
  return { keyId: body["keyId"], escrowB64: body["escrowB64"] };
}

/**
 * A two-person recovery: one holder files it, a second approves, and after the hold period a holder
 * carries it out. Until then the account owner can cancel it from the mail link.
 */
export interface RecoveryRequest {
  readonly id: string;
  readonly userId: string;
  readonly email: string | null;
  readonly requestedBy: string;
  readonly requestedByEmail: string | null;
  readonly resetPassword: boolean;
  readonly resetTwoFactor: boolean;
  readonly status: string;
  readonly createdAt: number;
  readonly executeAfter: number;
  readonly expiresAt: number;
  readonly requiredApprovals: number;
  readonly approvals: ReadonlyArray<{ readonly adminId: string; readonly email: string | null; readonly createdAt: number }>;
  readonly approved: boolean;
  /** Approved, hold period over and not expired: a holder can carry it out now. */
  readonly ready: boolean;
}

function isApproval(value: unknown): value is RecoveryRequest["approvals"][number] {
  return isRecord(value) && typeof value["adminId"] === "string" && isNullableString(value["email"]) && typeof value["createdAt"] === "number";
}

function isRecoveryRequest(value: unknown): value is RecoveryRequest {
  return isRecord(value) &&
    typeof value["id"] === "string" &&
    typeof value["userId"] === "string" &&
    isNullableString(value["email"]) &&
    typeof value["requestedBy"] === "string" &&
    isNullableString(value["requestedByEmail"]) &&
    typeof value["resetPassword"] === "boolean" &&
    typeof value["resetTwoFactor"] === "boolean" &&
    typeof value["status"] === "string" &&
    typeof value["createdAt"] === "number" &&
    typeof value["executeAfter"] === "number" &&
    typeof value["expiresAt"] === "number" &&
    typeof value["requiredApprovals"] === "number" &&
    Array.isArray(value["approvals"]) && value["approvals"].every(isApproval) &&
    typeof value["approved"] === "boolean" &&
    typeof value["ready"] === "boolean";
}

function requestFrom(body: unknown): RecoveryRequest {
  if (!isRecord(body) || !isRecoveryRequest(body["request"])) {
    throw unexpected();
  }
  return body["request"];
}

/** Files the reset; nothing changes until a second holder approves and the hold period is over. */
export async function recoverUser(
  userId: string,
  input: { keyId: string; saltB64: string; authKeyB64: string; keyEncB64: string; resetTwoFactor?: boolean }
): Promise<RecoveryRequest> {
  return requestFrom(await requestJson(`/api/v1/admin/users/${userId}/recover`, { method: "POST", body: input }));
}

/** Files a reset of the second factor only; the master password stays. */
export async function requestTwoFactorReset(userId: string): Promise<RecoveryRequest> {
  return requestFrom(await requestJson(`/api/v1/admin/users/${userId}/reset-2fa`, { method: "POST", body: {} }));
}

export async function fetchRecoveryRequests(): Promise<ReadonlyArray<RecoveryRequest>> {
  const body = await requestJson("/api/v1/admin/recovery/requests");
  if (!isRecord(body) || !Array.isArray(body["requests"]) || !body["requests"].every(isRecoveryRequest)) {
    throw unexpected();
  }
  return body["requests"];
}

export async function approveRecoveryRequest(id: string): Promise<RecoveryRequest> {
  return requestFrom(await requestJson(`/api/v1/admin/recovery/requests/${id}/approve`, { method: "POST", body: {} }));
}

export async function completeRecoveryRequest(id: string): Promise<AdminUser> {
  const body = await requestJson(`/api/v1/admin/recovery/requests/${id}/complete`, { method: "POST", body: {} });
  if (!isRecord(body) || !isAdminUser(body["user"])) {
    throw unexpected();
  }
  return body["user"];
}

export async function cancelRecoveryRequest(id: string): Promise<void> {
  await requestJson(`/api/v1/admin/recovery/requests/${id}/cancel`, { method: "POST", body: {} });
}

/** The owner's mail link: cancels without signing in. Returns the request's final status. */
export async function cancelRecoveryByToken(token: string): Promise<string> {
  const body = await requestJson("/api/v1/recovery-requests/cancel", { method: "POST", body: { token } });
  if (!isRecord(body) || body["ok"] !== true || typeof body["status"] !== "string") {
    throw unexpected();
  }
  return body["status"];
}

export async function enrollAdminRecovery(keyId: string, escrowB64: string): Promise<void> {
  const body = await requestJson("/api/v1/account/admin-recovery", { method: "PUT", body: { keyId, escrowB64 } });
  if (!isRecord(body) || body["ok"] !== true) {
    throw unexpected();
  }
}

export async function leaveAdminRecovery(): Promise<void> {
  const body = await requestJson("/api/v1/account/admin-recovery", { method: "DELETE" });
  if (!isRecord(body) || body["ok"] !== true) {
    throw unexpected();
  }
}
